import React, { useState, useEffect } from 'react'
import { PolarSurfer } from './PolarSurfer'
import { PolarBoxing3D } from './PolarBoxing3D'
import { ZenRunner } from './ZenRunner'
import { NeuroReflejos } from './NeuroReflejos'
import { CadenceRunner } from './CadenceRunner'
import { ACHIEVEMENTS_LIST, TROPHY_TIERS, getUnlockedAchievements } from './achievements'
import { IconActivity, IconZap, IconPostureWalking, IconMeditation } from '../../components/ui/Icons'

export function JuegosTab({ polar, demo, hrv, isFullscreen, setIsFullscreen, onUploadSession, onNavigate }) {
  const [activeGameId, setActiveGameId] = useState(null)
  const [unlocked, setUnlocked] = useState({})
  const [trophyFilter, setTrophyFilter] = useState('all') // 'all' | 'unlocked' | 'locked'

  // Cargar logros al montar y al volver al menú
  useEffect(() => {
    setUnlocked(getUnlockedAchievements())
  }, [activeGameId])

  // Si está jugando a Polar Surfer
  if (activeGameId === 'polar_surfer') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <PolarSurfer
          polar={polar}
          demo={demo}
          hrv={hrv}
          onExit={() => setActiveGameId(null)}
        />
      </div>
    )
  }

  // Si está jugando a Polar Boxing 3D
  if (activeGameId === 'polar_boxing') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <PolarBoxing3D
          polar={polar}
          demo={demo}
          hrv={hrv}
          onExit={() => setActiveGameId(null)}
        />
      </div>
    )
  }

  // Si está jugando a Nave Zen
  if (activeGameId === 'zen_runner') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <ZenRunner
          polar={polar}
          demo={demo}
          hrv={hrv}
          onExit={() => setActiveGameId(null)}
        />
      </div>
    )
  }

  // Si está jugando a Neuro-Reflejos
  if (activeGameId === 'neuro_reflejos') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <NeuroReflejos
          polar={polar}
          demo={demo}
          hrv={hrv}
          onExit={() => setActiveGameId(null)}
        />
      </div>
    )
  }

  // Si está jugando a Carrera de Cadencia
  if (activeGameId === 'cadence_runner') {
    return (
      <div style={{ maxWidth: 880, margin: '16px auto', padding: '0 12px' }}>
        <CadenceRunner
          polar={polar}
          demo={demo}
          hrv={hrv}
          onExit={() => setActiveGameId(null)}
        />
      </div>
    )
  }

  const unlockedCount = Object.keys(unlocked).length
  const totalTrophies = ACHIEVEMENTS_LIST.length
  const totalPoints = Object.keys(unlocked).reduce((sum, id) => {
    const ach = ACHIEVEMENTS_LIST.find(a => a.id === id)
    return sum + (ach?.points || 0)
  }, 0)

  const filteredAchievements = ACHIEVEMENTS_LIST.filter(ach => {
    const isUnl = !!unlocked[ach.id]
    if (trophyFilter === 'unlocked') return isUnl
    if (trophyFilter === 'locked') return !isUnl
    return true
  })

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '16px 14px 60px' }}>
      {/* ── 1. Encabezado de la Plataforma de Juegos ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: 12, marginBottom: 20
      }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--text)', margin: '0 0 4px', letterSpacing: -0.5 }}>
            Juegos y Biofeedback Cardiovascular
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>
            Entrená tu cuerpo y tu sistema nervioso jugando en tiempo real con el acelerómetro y pulso de tu Polar H10.
          </p>
        </div>

        {/* Chip de estado biomédico */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          background: 'var(--field-bg)', border: '1px solid var(--border)',
          borderRadius: 20, padding: '5px 12px'
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: (polar?.status === 'connected' || demo?.active) ? '#30d158' : '#ff9f0a',
            boxShadow: `0 0 6px ${(polar?.status === 'connected' || demo?.active) ? '#30d158' : '#ff9f0a'}`
          }} />
          <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text)' }}>
            {(polar?.status === 'connected') ? 'Polar H10 Conectado' : demo?.active ? 'Modo Demo Activo' : 'Sin sensor (Teclado/Táctil)'}
          </span>
        </div>
      </div>

      {/* ── 2. Hero Card Destacada: JUEGO ESTRELLA (Polar Surfer 3D) ── */}
      <div style={{
        background: 'radial-gradient(120% 120% at 85% 20%, rgba(0, 122, 255, 0.22) 0%, rgba(15, 20, 32, 0.95) 55%, var(--card-bg) 100%)',
        border: '1.5px solid rgba(0, 122, 255, 0.40)',
        borderRadius: 20, padding: '24px 22px', marginBottom: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
        position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ maxWidth: 560, position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 9.5, fontWeight: 900, background: '#30d158', color: '#000', padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
              Juego Estrella · 3D Runner
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#007aff' }}>
              Control por Acelerómetro Torácico
            </span>
          </div>

          <h2 style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', margin: '0 0 8px', letterSpacing: -0.6 }}>
            Polar Surfer: Bio-Runner 3D
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.5, margin: '0 0 16px' }}>
            Estilo Subway Surfers controlado con los sensores de tu Polar H10: <strong>incliná tu torso a los lados</strong> para cambiar de carril y <strong>pegá un salto en el lugar</strong> para esquivar las vallas. Si mantenés el pulso sereno (&lt;75 BPM), se activa el <strong>imán de monedas automático</strong>.
          </p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--chip-bg)', color: 'var(--text-soft)', padding: '4px 9px', borderRadius: 8 }}>
              Inclinación Eje Y
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--chip-bg)', color: 'var(--text-soft)', padding: '4px 9px', borderRadius: 8 }}>
              Salto Eje X
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--chip-bg)', color: 'var(--text-soft)', padding: '4px 9px', borderRadius: 8 }}>
              Imán de Monedas por Calma
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'var(--chip-bg)', color: 'var(--text-soft)', padding: '4px 9px', borderRadius: 8 }}>
              Teclado / Swipe táctil
            </span>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveGameId('polar_surfer')}
              style={{
                background: '#007aff', color: '#ffffff', border: 'none',
                borderRadius: 12, padding: '12px 28px', fontSize: 14, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8,
                boxShadow: '0 4px 18px rgba(0, 122, 255, 0.40)', transition: 'transform 0.15s ease'
              }}
            >
              <span>▶</span>
              <span>¡JUGAR POLAR SURFER!</span>
            </button>
            <button
              onClick={() => setActiveGameId('zen_runner')}
              style={{
                background: 'var(--chip-bg)', color: 'var(--text)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '11px 20px', fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6
              }}
            >
              <span>▲</span>
              <span>Jugar Nave Zen</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── 3. Catálogo Completo de Juegos de Biofeedback ── */}
      <div style={{ marginBottom: 32 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: 0.6, margin: '0 0 12px' }}>
          Catálogo Completo de Juegos
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
          {/* Juego 2: Polar Boxing 3D */}
          <div style={{
            background: 'var(--card-bg)', border: '1.5px solid rgba(239, 68, 68, 0.45)', borderRadius: 16,
            padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            boxShadow: '0 4px 18px rgba(239, 68, 68, 0.10)'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, background: '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                  Ring 3D en Three.js
                </span>
                <span style={{ fontSize: 13, fontWeight: 900, color: '#ef4444' }}>🥊 BOX</span>
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px' }}>
                Polar Boxing 3D: Sombra y Ring
              </h4>
              <p style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.45, margin: 0 }}>
                Escenografía de estadio con multitud y flashes. Esquivá con el torso, hacé footwork en punta de pie y meté contragolpes secos con el pecho.
              </p>
            </div>
            <button
              onClick={() => setActiveGameId('polar_boxing')}
              style={{
                background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 18px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                fontFamily: 'inherit', marginTop: 14, alignSelf: 'flex-start',
                boxShadow: '0 2px 10px rgba(239, 68, 68, 0.3)'
              }}
            >
              ¡SUBIR AL RING 3D!
            </button>
          </div>

          {/* Juego 3: Neuro-Reflejos bajo Presión */}
          <div style={{
            background: 'var(--card-bg)', border: '1.5px solid rgba(0, 122, 255, 0.35)', borderRadius: 16,
            padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            boxShadow: '0 4px 18px rgba(0, 122, 255, 0.08)'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, background: '#007aff', color: '#fff', padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                  Control de Estrés
                </span>
                <IconZap size={18} color="#007aff" />
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px' }}>
                Neuro-Reflejos bajo Presión
              </h4>
              <p style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.45, margin: 0 }}>
                Dianas reactivas a toda velocidad. Mantené tu pulso en calma (&le;80 BPM) para activar el Foco Quirúrgico con multiplicador x3 de puntos.
              </p>
            </div>
            <button
              onClick={() => setActiveGameId('neuro_reflejos')}
              style={{
                background: '#007aff', color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 18px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                fontFamily: 'inherit', marginTop: 14, alignSelf: 'flex-start',
                boxShadow: '0 2px 10px rgba(0, 122, 255, 0.3)'
              }}
            >
              ¡TEST DE REFLEJOS!
            </button>
          </div>

          {/* Juego 4: Carrera de Cadencia */}
          <div style={{
            background: 'var(--card-bg)', border: '1.5px solid rgba(48, 209, 88, 0.40)', borderRadius: 16,
            padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            boxShadow: '0 4px 18px rgba(48, 209, 88, 0.08)'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, background: '#30d158', color: '#000', padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                  Running & Cinta
                </span>
                <IconPostureWalking size={18} color="#30d158" />
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px' }}>
                Carrera de Cadencia (Zona 2)
              </h4>
              <p style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.45, margin: 0 }}>
                Sincronizá tus pasos al metrónomo biomecánico y corré en Zona 2 aeróbica para cargar el Reactor al 100% y activar la Sobremarcha x4.
              </p>
            </div>
            <button
              onClick={() => setActiveGameId('cadence_runner')}
              style={{
                background: '#30d158', color: '#000', border: 'none', borderRadius: 8,
                padding: '9px 18px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                fontFamily: 'inherit', marginTop: 14, alignSelf: 'flex-start',
                boxShadow: '0 2px 10px rgba(48, 209, 88, 0.3)'
              }}
            >
              ¡ENTRENAR CADENCIA!
            </button>
          </div>

          {/* Juego 5: Nave Zen */}
          <div style={{
            background: 'var(--card-bg)', border: '1.5px solid rgba(100, 210, 255, 0.35)', borderRadius: 16,
            padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            boxShadow: '0 4px 18px rgba(100, 210, 255, 0.08)'
          }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, background: '#64d2ff', color: '#000', padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }}>
                  Altitud & Calma
                </span>
                <IconMeditation size={18} color="#64d2ff" />
              </div>
              <h4 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: '0 0 4px' }}>
                Nave Zen: Túnel Cibernético
              </h4>
              <p style={{ fontSize: 12, color: 'var(--text-faint)', lineHeight: 1.45, margin: 0 }}>
                Pilotá la nave a través del túnel con el pulso o espacio. Mantener la serenidad multiplica tus orbes de energía por x3.
              </p>
            </div>
            <button
              onClick={() => setActiveGameId('zen_runner')}
              style={{
                background: 'rgba(100, 210, 255, 0.18)', color: '#64d2ff', border: '1px solid #64d2ff', borderRadius: 8,
                padding: '9px 18px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                fontFamily: 'inherit', marginTop: 14, alignSelf: 'flex-start'
              }}
            >
              ¡VOLAR EN NAVE ZEN!
            </button>
          </div>
        </div>
      </div>

      {/* ── 4. Sala de Trofeos y Sistema de Logros ── */}
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 18,
        padding: '20px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
      }}>
        {/* Cabecera de la Sala de Trofeos */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12, marginBottom: 16
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ fontSize: 16 }}>★</span>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', margin: 0, letterSpacing: -0.2 }}>
                Sala de Trofeos y Logros
              </h3>
            </div>
            <span style={{ fontSize: 11.5, color: 'var(--text-faint)', display: 'block', marginTop: 2 }}>
              {unlockedCount} de {totalTrophies} Trofeos Desbloqueados · {totalPoints} Puntos acumulados
            </span>
          </div>

          {/* Filtros de trofeos */}
          <div style={{ display: 'flex', background: 'var(--field-bg)', borderRadius: 8, padding: 2, border: '1px solid var(--border-soft)' }}>
            <button
              onClick={() => setTrophyFilter('all')}
              style={{
                background: trophyFilter === 'all' ? 'var(--card-bg)' : 'transparent',
                color: trophyFilter === 'all' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Todos ({totalTrophies})
            </button>
            <button
              onClick={() => setTrophyFilter('unlocked')}
              style={{
                background: trophyFilter === 'unlocked' ? 'var(--card-bg)' : 'transparent',
                color: trophyFilter === 'unlocked' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Desbloqueados ({unlockedCount})
            </button>
            <button
              onClick={() => setTrophyFilter('locked')}
              style={{
                background: trophyFilter === 'locked' ? 'var(--card-bg)' : 'transparent',
                color: trophyFilter === 'locked' ? 'var(--text)' : 'var(--text-faint)',
                border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit'
              }}
            >
              Por Desbloquear ({totalTrophies - unlockedCount})
            </button>
          </div>
        </div>

        {/* Barra de Progreso General */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ height: 8, background: 'var(--track)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.round((unlockedCount / totalTrophies) * 100)}%`, height: '100%',
              background: 'linear-gradient(90deg, #007aff, #ffd700)', borderRadius: 4,
              transition: 'width 0.4s ease'
            }} />
          </div>
        </div>

        {/* Grid de Trofeos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
          {filteredAchievements.map(ach => {
            const isUnl = !!unlocked[ach.id]
            const tierMeta = TROPHY_TIERS[ach.tier] || TROPHY_TIERS.bronze
            return (
              <div
                key={ach.id}
                style={{
                  background: isUnl ? tierMeta.bg : 'var(--field-bg)',
                  border: `1px solid ${isUnl ? tierMeta.color + '55' : 'var(--border-soft)'}`,
                  borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                  opacity: isUnl ? 1 : 0.65, transition: 'all 0.2s ease'
                }}
              >
                {/* Medalla / Icono */}
                <div style={{
                  width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  background: isUnl ? tierMeta.color : 'rgba(255,255,255,0.08)',
                  color: isUnl ? '#000' : 'var(--text-faint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 900,
                  boxShadow: isUnl ? `0 0 12px ${tierMeta.color}66` : 'none'
                }}>
                  ★
                </div>

                {/* Texto del Trofeo */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 800, color: isUnl ? 'var(--text)' : 'var(--text-muted)' }}>
                      {ach.title}
                    </span>
                    <span style={{
                      fontSize: 9.5, fontWeight: 800, color: tierMeta.color,
                      background: isUnl ? `${tierMeta.color}25` : 'transparent',
                      padding: '1px 5px', borderRadius: 4
                    }}>
                      {tierMeta.name} · +{ach.points}
                    </span>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 0', lineHeight: 1.35 }}>
                    {ach.subtitle}
                  </p>
                  {isUnl && (
                    <span style={{ fontSize: 9.5, color: '#30d158', fontWeight: 700, display: 'block', marginTop: 4 }}>
                      ✓ Desbloqueado el {new Date(unlocked[ach.id].unlockedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
